import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Loader } from '../../../App/components/Loader';
import TriangleIcon from '../../../../images/down-filled-triangle.svg';

/**
 * A cascading submenu item for the Nav dropdowns. Renders with the same `nav__dropdown-item` /
 * dropdown styling as the rest of the menu, but opens its child list to the SIDE (a flyout) on
 * hover / focus / click, so submenus can nest. It manages its own open state (the shared Menubar
 * only supports one level), calls `onOpen` once on first open for lazy loading, and shows the
 * standard <Loader/> while `loading` is true.
 */
const NavFlyout = ({
  label,
  loading,
  disabled,
  onOpen,
  triggerProps,
  children
}) => {
  const [open, setOpen] = useState(false);
  const openedOnce = useRef(false);
  const triggerRef = useRef(null);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    if (!openedOnce.current) {
      openedOnce.current = true;
      if (onOpen) onOpen();
    }
  }, [disabled, onOpen]);

  // Move focus onto our own trigger as the pointer enters, the same way the shared MenubarItems
  // focus-follow-the-mouse. Otherwise the previously-hovered sibling keeps its :focus styling and
  // looks stuck-hovered once the pointer moves onto this custom flyout.
  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    handleOpen();
    if (triggerRef.current) triggerRef.current.focus();
  }, [disabled, handleOpen]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowRight':
        e.preventDefault();
        handleOpen();
        break;
      case 'Escape':
      case 'ArrowLeft':
        if (open) {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
          if (triggerRef.current) triggerRef.current.focus();
        }
        break;
      default:
        break;
    }
  };

  return (
    <li
      className={classNames(
        'nav__dropdown-item',
        'nav__dropdown-item--flyout',
        open && 'nav__dropdown-item--flyout--open',
        disabled && 'nav__dropdown-item--disabled'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleClose}
    >
      <button
        type="button"
        ref={triggerRef}
        className="nav__flyout-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled}
        onClick={() => (open ? handleClose() : handleOpen())}
        onFocus={(e) => {
          handleOpen();
          // Let the Menubar treat this like one of its own items on focus — chiefly to cancel the
          // hide-timeout its blur handler schedules when the previously focused item lost focus.
          if (triggerProps.onFocus) triggerProps.onFocus(e);
        }}
        onBlur={triggerProps.onBlur}
        onKeyDown={handleKeyDown}
      >
        <span>{label}</span>
        <TriangleIcon
          className="nav__flyout-caret"
          focusable="false"
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul className="nav__submenu" role="menu">
          {loading ? (
            <li className="nav__flyout-status">
              <Loader />
            </li>
          ) : (
            children
          )}
        </ul>
      )}
    </li>
  );
};

NavFlyout.propTypes = {
  label: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onOpen: PropTypes.func,
  // onFocus/onBlur borrowed from the Menubar so the trigger cooperates with its open/hide logic.
  triggerProps: PropTypes.shape({
    onFocus: PropTypes.func,
    onBlur: PropTypes.func
  }),
  children: PropTypes.node
};

NavFlyout.defaultProps = {
  loading: false,
  disabled: false,
  onOpen: undefined,
  triggerProps: {},
  children: null
};

export default NavFlyout;
